"use client";

import Image from "next/image";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import {
  LuSmartphone,
  LuFolderOpen,
  LuClock,
  LuMap,
  LuGlobe,
  LuGraduationCap,
  LuListChecks,
  LuNewspaper,
  LuMessageCircle,
  LuChevronDown,
  LuCircleCheck,
} from "react-icons/lu";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.xephyrlabs.lattuai";

const whyCards = [
  {
    icon: LuSmartphone,
    title: "Plan from anywhere",
    body: "Check and work on your study abroad plan from your phone whenever you need it.",
  },
  {
    icon: LuFolderOpen,
    title: "Keep your research in one place",
    body: "Research countries, universities, programs, scholarships, and news, and connect with others for any confusions from the same app.",
  },
  {
    icon: LuClock,
    title: "Move at your own pace",
    body: "You do not need to figure everything out in one sitting. Come back when you are ready.",
  },
];

const featureCards = [
  {
    icon: LuMap,
    title: "Create your journey plan",
    body: "Answer a few questions about your goals, budget, and preferences, and get a plan built around you.",
  },
  {
    icon: LuGlobe,
    title: "Explore countries",
    body: "Compare study destinations by costs, process, requirements, benefits, and student life.",
  },
  {
    icon: LuGraduationCap,
    title: "Browse scholarships",
    body: "Find scholarship opportunities across countries without searching through dozens of websites.",
  },
  {
    icon: LuListChecks,
    title: "Application Tracker",
    body: "Track applications, what documents you need, and stay on top of deadlines.",
  },
  {
    icon: LuNewspaper,
    title: "Read important updates",
    body: "Stay informed about visa, policy, and study-abroad news that may affect your plans.",
  },
  {
    icon: LuMessageCircle,
    title: "Join the community",
    body: "Ask questions, share what you learn, and connect with others planning a similar journey.",
  },
];

const whoCards = [
  {
    title: "You are confused about where to start",
    body: "Start with a plan built around your goals and preferences.",
  },
  {
    title: "You are comparing countries",
    body: "See costs, processes, requirements, and options side by side.",
  },
  {
    title: "You are looking for scholarships",
    body: "Explore opportunities and understand how funding fits into your larger plan.",
  },
  {
    title: "You want to stay updated",
    body: "Read updates that matter for study-abroad planning.",
  },
];

const faqs = [
  {
    q: "Is the Lattu AI app free?",
    a: "Yes. All current features on the platform are free to use and will always remain free. Advanced personalization, comparisons, and expert services may be offered through premium features in the future.",
  },
  {
    q: "Is the app available on iPhone?",
    a: "The Android app is available on Google Play. The iOS version is coming soon.",
  },
  {
    q: "Can I use Lattu AI without downloading the app?",
    a: "Yes. You can use Lattu AI from the website and create your journey plan there.",
  },
  {
    q: "What can I do in the app?",
    a: "You can create your study abroad journey plan, explore and compare countries, find scholarships, learn about recent news regarding study abroad, and connect with a community of peers who are also on the same path as you.",
  },
  {
    q: "Is this only for students?",
    a: "Right now, Lattu AI is focused on Bangladeshi students preparing to study abroad. We plan to introduce more options for work, PR, and travel in the near future.",
  },
  {
    q: "Does Lattu apply to universities for me?",
    a: "Not yet. Lattu AI helps you plan first, understand your options, and make more informed decisions. We plan to introduce university applications from the platform in the near future.",
  },
];

const GooglePlayButton = ({
  placement,
  ctaText,
}: {
  placement: string;
  ctaText: string;
}) => {
  const posthog = usePostHog();
  return (
    <a
      className="flex items-center gap-3 px-5 py-3 bg-black dark:bg-white/10 border border-white/20 opacity-70 rounded-xl hover:scale-105 transition-transform"
      href="https://play.google.com/store/apps/details?id=com.xephyrlabs.lattuai"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        posthog?.capture("app_download_click", {
          app_platform: "android",
          placement,
          source_page: "app_page",
          cta_text: ctaText,
        })
      }
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white shrink-0">
        <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.28-2.7-2.7zM.29 1.03C.1 1.35 0 1.73 0 2.15v19.7c0 .42.1.8.29 1.12l.06.06 11.04-11.04v-.26L.35.97zM20.38 10.4l-2.92-1.69-3.03 3.03 3.03 3.03 2.94-1.7c.84-.49.84-1.28-.02-1.67zM4.17.24l12.6 7.28-2.7 2.7z" />
      </svg>
      <div className="text-left">
        <p className="text-white/60 text-[10px] leading-none">Get it on</p>
        <p className="text-white font-semibold text-sm leading-tight">
          Google Play
        </p>
      </div>
    </a>
  );
};

const AppPageContent = () => {
  const posthog = usePostHog();

  return (
    <main className="overflow-hidden">
      {/* Section 1: Hero */}
      <section className="pb-24 pt-16 md:pt-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-5xl lg:text-6xl font-black text-black dark:text-white leading-[1.1] mb-6 tracking-tight">
              Lattu AI is now on your phone
            </h1>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <GooglePlayButton
                placement="app_page_hero"
                ctaText="get_it_on_google_play"
              />
            </div>
            <p className="text-xs text-zinc-500">iOS version coming soon.</p>
          </div>
          <div className="lg:col-span-6 relative flex justify-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-indigo-500/30 blur-[80px] pointer-events-none" />
            <div className="absolute top-1/4 left-1/3 w-40 h-40 rounded-full bg-violet-500/20 blur-[60px] pointer-events-none" />
            <Image
              src="/images/app_banner.png"
              alt="Lattu AI mobile app"
              width={1240}
              height={880}
              className="relative z-10 drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Section 2: Why use the app? */}
      <section className="py-24 bg-[#eceff1] dark:bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <h2 className="text-4xl font-black text-black dark:text-white mb-4">
              Plan when it&apos;s convenient for you
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Studying abroad comes with a lot to think through. The app helps
              you come back to your plan easily on your phone, compare options,
              and check important information instead of having your research
              scattered across your laptop, phone, and notebooks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyCards.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-white dark:bg-black p-8 rounded-lg border border-zinc-200 dark:border-indigo-500/20 shadow-sm"
              >
                <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center mb-5">
                  <Icon size={22} className="text-indigo-500" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-black dark:text-white">
                  {title}
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: What you can do in the app */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-black dark:text-white mb-4">
              What&apos;s inside the Lattu AI app?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featureCards.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="p-8 rounded-lg border border-zinc-200 dark:border-indigo-500/20 bg-white dark:bg-transparent shadow-sm"
              >
                <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center mb-5">
                  <Icon size={22} className="text-indigo-500" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-black dark:text-white">
                  {title}
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Who it is for */}
      <section className="py-24 bg-[#eef0ff] dark:bg-[#0a0d1a]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-4xl font-black text-black dark:text-white mb-4">
              Built for Bangladeshi students planning to study abroad
            </h2>
            <p className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
              Whether you&apos;re just beginning your research or comparing
              countries, universities, and costs, Lattu AI helps you move
              forward with more confidence.
            </p>
          </div>

          <div className="max-w-3xl mx-auto rounded-3xl bg-white dark:bg-[#111827] border border-indigo-100 dark:border-indigo-500/20 shadow-lg p-8 sm:p-10">
            <ul className="space-y-7">
              {whoCards.map(({ title, body }) => (
                <li key={title} className="flex items-start gap-4 group">
                  <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                    <LuCircleCheck className="h-5 w-5" />
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-black dark:text-white">
                      {title}
                    </h3>
                    <p className="mt-1 text-zinc-600 dark:text-zinc-400 leading-relaxed">
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-indigo-500">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-lg mb-10 max-w-2xl mx-auto font-medium text-white">
            You can use Lattu AI from the website or the Android app. Start your
            journey wherever it feels easier, and come back to your plan when
            you need it.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-4">
            <a
              className="inline-block px-10 py-4 rounded-lg font-black text-lg hover:brightness-110 active:scale-95 transition-all shadow-xl bg-white text-indigo-500 shadow-black/10"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                posthog?.capture("app_download_click", {
                  app_platform: "android",
                  placement: "app_page_bottom_cta",
                  source_page: "app_page",
                  cta_text: "get_the_android_app",
                })
              }
            >
              Get the Android App
            </a>
            <Link
              className="inline-block px-10 py-4 rounded-lg font-black text-lg border-2 border-white text-white hover:bg-white/10 active:scale-95 transition-all"
              href="/login/"
            >
              Create Your Journey on Web
            </Link>
          </div>
        </div>
      </section>

      {/* Section 6: FAQ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-4xl font-black text-black dark:text-white mb-10 text-center">
            FAQ
          </h2>
          <div className="flex flex-col gap-3">
            {faqs.map(({ q, a }) => (
              <details
                key={q}
                className="group rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-transparent px-6 py-4"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-bold text-black dark:text-white">
                  {q}
                  <LuChevronDown
                    size={18}
                    className="shrink-0 text-indigo-500 transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed mt-3">
                  {a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default AppPageContent;
